import {keepPreviousData, queryOptions} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'

import type {CopyProjectOwnersResponse} from '../components/projects/types'

const COPY_PROJECT_OWNERS_QUERY_KEY = 'copy-project-owners'

/**
 * The owner set rarely changes between dialog opens, so we cache for 5 minutes
 * to avoid a refetch each time the user re-opens the Copy dialog.
 */
const COPY_PROJECT_OWNERS_STALE_TIME_MS = 5 * 60 * 1000

interface CopyProjectOwnersRequest {
  /**
   * `MemexProject#id` (not the `number`).
   */
  projectId: number
  /**
   * When `true`, requests the templates-flavored owner list.
   */
  copyAsTemplate: boolean
  q?: string
}

/**
 * Query options for the "Owner" picker entries in the Copy / Copy as template dialogs.
 */
export function copyProjectOwnersQueryOptions({projectId, copyAsTemplate, q = ''}: CopyProjectOwnersRequest) {
  const trimmedQuery = q.trim()
  return queryOptions({
    queryKey: [COPY_PROJECT_OWNERS_QUERY_KEY, projectId, copyAsTemplate, trimmedQuery] as const,
    staleTime: COPY_PROJECT_OWNERS_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    queryFn: async ({signal}) => {
      const params = new URLSearchParams()
      if (copyAsTemplate) {
        params.set('copy_as_template', 'true')
      }
      if (trimmedQuery) {
        params.set('q', trimmedQuery)
      }
      const search = params.toString()
      const url = `/memexes/${projectId}/suggested_organizations${search ? `?${search}` : ''}`

      const response = await reactFetchJSON(url, {signal})
      if (!response.ok) {
        throw new Error(`Failed to fetch copy project owners: ${response.status}`)
      }
      return (await response.json()) as CopyProjectOwnersResponse
    },
  })
}
